import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ui.css";

// A button that opens a panel under it. Closes on outside click, Escape, or
// when the page scrolls. The panel is drawn at the top of the document, so a
// scrolling container (like the table) never clips it.
// `children` may be a function, which receives `close`.
export default function Popover({ trigger, triggerClass = "btn", align = "left", label, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const place = () => {
    const r = buttonRef.current?.getBoundingClientRect();
    if (!r) return;
    setPos(
      align === "right"
        ? { top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) }
        : { top: r.bottom + 6, left: Math.max(8, r.left) }
    );
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep it on screen: if it would run off the right edge, pull it back
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel || pos?.left == null) return;
    const overflow = panel.getBoundingClientRect().right - (window.innerWidth - 8);
    if (overflow > 0) setPos((p) => ({ ...p, left: Math.max(8, p.left - overflow) }));
  }, [open, pos]);

  useEffect(() => {
    if (!open) return undefined;
    const inside = (target) =>
      buttonRef.current?.contains(target) || panelRef.current?.contains(target);
    const onDown = (e) => {
      if (!inside(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onScroll = (e) => {
      if (!panelRef.current?.contains(e.target)) setOpen(false);
    };
    const onResize = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="popover">
      <button
        ref={buttonRef}
        type="button"
        className={`${triggerClass}${open ? " is-open" : ""}`}
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      {open &&
        pos &&
        createPortal(
          <div className="popover-panel" role="dialog" ref={panelRef} style={pos}>
            {typeof children === "function" ? children(close) : children}
          </div>,
          document.body
        )}
    </div>
  );
}
